import { AdminLoginResDto } from "../../../api/estabrookSchemas";
import { fetchAdminAuthControllerRefresh } from "../../../api/estabrookComponents";
import { Tokens } from "../../../types/models/tokens";
import { sessionFromLoginRes } from "../../../types/models/session";
import { useAuthStore } from "./auth-store";

/**
 * Persists tokens and syncs the auth store from any endpoint that returns the
 * AdminLoginResDto shape - login, silent token refresh (via
 * estabrookContext.ts and refreshAuthResponse below), and (once wired up)
 * impersonation start/stop.
 *
 * Centralizing this prevents call sites from persisting tokens without also
 * syncing `role` / `organizationId` / `isImpersonating` - which previously
 * happened on token refresh, leaving the session (and the X-Organization-Id
 * header derived from it) stale until the user's refresh token expired and
 * they logged in again.
 */
export function applyAuthResponse(dto: AdminLoginResDto): Tokens {
  const tokens = Tokens.create(dto);
  tokens.persist();

  const { setIsLoggedIn, setCustomer, setSession } = useAuthStore.getState();
  setIsLoggedIn(true);
  setCustomer(dto.customer);
  setSession(sessionFromLoginRes(dto));

  return tokens;
}

/**
 * Refreshes the access/refresh tokens and routes the response through
 * applyAuthResponse, so callers outside the react-query fetcher (e.g. the
 * websocket reconnect flow in use-socket-events.ts) also get `role` /
 * `organizationId` / `isImpersonating` re-synced instead of only persisting
 * new tokens. Mirrors the retry/error handling of the old `Tokens.refresh()`,
 * which this replaces.
 */
export async function refreshAuthResponse(): Promise<Tokens | null> {
  const tokens = Tokens.retrieveFromLocalStorage();
  if (!tokens) return null;

  if (tokens.isExpired(true)) {
    Tokens.destroy();
    return null;
  }

  try {
    const response = await fetchAdminAuthControllerRefresh({
      body: { refreshToken: tokens.refreshToken },
    });

    if (response.data?.accessToken && response.data?.refreshToken) {
      return applyAuthResponse(response.data);
    }

    Tokens.destroy();
    return null;
  } catch (error) {
    console.error("Token refresh failed:", error);
    Tokens.destroy();
    return null;
  }
}
